
import { computed, defineComponent, reactive } from 'vue';
import { IOptionString } from '@/core/models/AntDesign/IOption';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import storeSistema from '@/store/storeSistema';
import ServicoPersonalizacaoTela from '@/servicos/MeuSistema/ServicoPersonalizacaoTela';

export default defineComponent({
  name: 'SelecionarItemCampoPersonalizado',
  props: {
    varios: {
      type: Boolean,
      default: false,
    },
    codigoSelecionado: {
      type: String || undefined,
    },
    codigosSelecionados: {
      type: Array as () => string[],
      default: () => [],
    },
    codigoCampoPersonalizado: {
      type: Number,
      required: true,
    },
    empresas: {
      type: Array as () => number[],
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Digite para selecionar',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'change'],
  setup(props, { emit }) {
    const { montaOpcoesComListaConsultaRapidaString } = useTelaBase();

    let debounce = 0;
    const servicoPersonalizacaoTela = new ServicoPersonalizacaoTela();

    const state = reactive({
      listaItens: [] as IOptionString[],
      buscandoDados: false,
      emDigitacao: false,
    });

    async function obterItemCampoPersonalizadoPorCodigo(valor: any) {
      if (state.emDigitacao) { return; }
      if (!UtilitarioGeral.valorValido(valor)) { return; }

      if (props.varios) {
        if (props.codigosSelecionados.length === 0) { return; }
      } else if (props.codigoSelecionado === '' || props.codigoSelecionado === '0') {
        return;
      }
      let efetuarConsulta = true;

      if (UtilitarioGeral.validaLista(state.listaItens)) {
        if (props.varios) {
          const listaCodigosConsulta = state.listaItens.map((c) => c.value);
          const diferencas = props.codigosSelecionados.filter((c) => !listaCodigosConsulta.includes(c));
          if (!UtilitarioGeral.validaLista(diferencas)) {
            efetuarConsulta = false;
          }
        } else {
          const existeLista = state.listaItens.find((c) => c.value === props.codigoSelecionado);
          if (existeLista !== undefined) {
            efetuarConsulta = false;
          }
        }
      }

      if (!efetuarConsulta) return;

      state.listaItens = [];
      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        apenasAtivos: false, recursoAssociado: '',
      };

      if (UtilitarioGeral.validaLista(props.empresas)) {
        parametrosConsultaRapida.empresas = props.empresas;
      } else {
        parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
      }

      if (props.varios) {
        parametrosConsultaRapida.filtrarPorVariosCodigos = true;
        parametrosConsultaRapida.valores = valor;
      } else {
        parametrosConsultaRapida.filtrarPorCodigo = true;
        parametrosConsultaRapida.valor = valor;
      }

      const listaItens = await servicoPersonalizacaoTela.consultaRapidaItensCampos(props.codigoCampoPersonalizado, parametrosConsultaRapida);
      state.listaItens = montaOpcoesComListaConsultaRapidaString(listaItens);
      if (!UtilitarioGeral.validaLista(listaItens)) {
        if (props.varios) {
          emit('update:codigosSelecionados', undefined);
        } else {
          emit('update:codigoSelecionado', undefined);
        }
      }
    }

    async function pesquisarItemCampoPersonalizado(valorPesquisa: any) {
      state.emDigitacao = true;

      state.buscandoDados = true;
      state.listaItens = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida: IParametrosConsultaRapida = {
          valor: valorPesquisa, apenasAtivos: true, recursoAssociado: '',
        };

        if (UtilitarioGeral.validaLista(props.empresas)) {
          parametrosConsultaRapida.empresas = props.empresas;
        } else {
          parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
        }

        const listaItens = await servicoPersonalizacaoTela.consultaRapidaItensCampos(props.codigoCampoPersonalizado, parametrosConsultaRapida);
        state.buscandoDados = false;
        state.listaItens = montaOpcoesComListaConsultaRapidaString(listaItens);
      }, 600);
    }

    function obterItemCampoPersonalizado():any {
      obterItemCampoPersonalizadoPorCodigo(props.codigoSelecionado);
      return props.codigoSelecionado;
    }

    const computedCodigoSelecionado = computed({
      get: () => obterItemCampoPersonalizado(),
      set: (val: string) => {
        emit('update:codigoSelecionado', val);
      },
    });

    function obterItensCampoPersonalizado():string[] {
      obterItemCampoPersonalizadoPorCodigo(props.codigosSelecionados);
      return props.codigosSelecionados;
    }

    const computedCodigosSelecionados = computed({
      get: () => obterItensCampoPersonalizado(),
      set: (val: string[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    function change() {
      emit('change', props.varios ? props.codigosSelecionados : props.codigoSelecionado);
    }

    return {
      props,
      state,
      pesquisarItemCampoPersonalizado,
      computedCodigoSelecionado,
      computedCodigosSelecionados,
      change,
    };
  },
});
